import { isPseudoVendorName } from '../../utils/poSendStatus';

/**
 * PO generation (`vendorPurchaseOrder.service.js` `createFromConfirmedOrder`) writes
 * `vendorId: null, vendorEmail: ''` whenever a supplier NAME resolves to nobody in the
 * vendor register — an unsendable "ghost" PO. Generation reports this back as
 * `unresolvedVendors` at the moment it happens, but that toast is seen only by whoever
 * clicked Generate; nothing about it survives a reload for whoever sends the PO tomorrow.
 *
 * This is the one, pure derivation of "does this stored PO have that problem right now" —
 * `vendorId == null` AND `vendorName` is a real, non-blank, non-placeholder name. No new
 * stored field and no new endpoint: everything needed is already on the PO document. Used
 * by `VendorPurchaseOrdersPage.tsx` (count + per-row marker) and by anything else that
 * needs the same answer, so there is one owner rather than a second copy of the check
 * growing inline in a page.
 *
 * `isPseudoVendorName` is the registered owner of "which vendor names are placeholders"
 * (BUSINESS_RULES#pseudo-vendor-names, mirrored from `PSEUDO_VENDOR_NAMES` in
 * `vendorPurchaseOrder.service.js`) — imported, never re-spelled here.
 *
 * Deliberately excludes `status: 'cancelled'` POs: a cancelled PO is never going to be
 * sent to anybody, so a missing vendor record on it is not something anyone needs to fix.
 */
export interface VendorGapPoLike {
  vendorId?: { _id: string } | string | null;
  vendorName?: string;
  status?: string;
}

/**
 * True when a PO was raised for a real (non-blank, non-pseudo) supplier name but never
 * resolved to a `Vendor` record — it has no `vendorId`, so it cannot be emailed until
 * someone picks one.
 */
export function isUnresolvedVendorPo(po: VendorGapPoLike): boolean {
  if (po.status === 'cancelled') return false;
  if (po.vendorId != null) return false; // already has a vendor record
  const name = String(po.vendorName || '').trim();
  if (!name) return false; // blank name is a different, already-handled state (missingVendors) — never got a PO at all
  return !isPseudoVendorName(name);
}

/** How many of a list of POs have no vendor record, per `isUnresolvedVendorPo`. */
export function countUnresolvedVendorPos(pos: VendorGapPoLike[]): number {
  return pos.filter(isUnresolvedVendorPo).length;
}
